import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ELAutocompleteElement } from '@el-autocomplete';
import { map, startWith, take } from 'rxjs/operators';
import { firstValueFrom, Subject } from 'rxjs';

import {
  IDropdownField,
  IFormFieldOption,
  IReference,
  IReferenceCategoryResponse,
} from '@shared/interfaces';

import {
  ReferenceCategoryService,
  ReferenceService,
} from '../../../../../references/services';

@Component({
  selector: 'app-drop-down',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  standalone: false,
})
export class DropDownComponent implements OnInit, OnDestroy {
  @Input() dropDown: IDropdownField;
  @Input() control: FormControl;
  @Input() externalToken?: string | null; // Token for external user authentication
  @Output() valueChange = new EventEmitter<IFormFieldOption>();

  constructor(
    private readonly referenceService: ReferenceService,
    private readonly referenceCategoryService: ReferenceCategoryService
  ) {}

  items: IFormFieldOption[] = [];
  isExternalSelection = false;
  isInternalSelection = false;
  filteredExternalReferenceOptions: ELAutocompleteElement<IReference>[] = [];
  referenceCategories: IReferenceCategoryResponse[];
  externalOptions: ELAutocompleteElement<IReference>[] = [];
  filteredExternalOptions: ELAutocompleteElement<IReference>[] = [];

  externalField: string;
  isLoading = false;
  externalCategory: string;
  externalDisplayValue = '';
  private readonly displayValueSubject = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.listenToReferences(() => {
      this.initForm();
      if (this.dropDown.valueSource === 'external_references') {
        // Ensure externalCategory and externalField are set before loading references
        if (this.externalCategory && this.externalField) {
          this.loadExternalReferences();
        } else {
          console.error(
            'External reference dropdown missing category or field:',
            {
              externalCategory: this.externalCategory,
              externalField: this.externalField,
              dropDown: this.dropDown,
            }
          );
        }
      } else {
        console.log(
          'Not an external reference dropdown, valueSource:',
          this.dropDown.valueSource
        );
      }
    });

    // Emit whenever value changes
    this.control.valueChanges.subscribe((val) => {
      if (
        val?.length &&
        !this.externalDisplayValue?.toString()?.trim()?.length
      ) {
        this.referenceService
          .getFieldValueFromBackend({
            referenceId: val,
            fieldId: this.externalField,
          })
          .then((value) => {
            const displayValue = value?.value?.toString() ?? '';
            this.externalDisplayValue = displayValue;
          });
      }

      const selectedItem = this.items.find((i) => i.value === val);

      this.valueChange.emit(selectedItem);
    });
  }

  initForm() {
    if (this.dropDown.isRequired) {
      this.control.setValidators([Validators.required]);
      this.control.updateValueAndValidity();
    }

    if (this.dropDown.valueSource === 'internal') {
      this.isInternalSelection = true;
      this.items = this.dropDown.internalOptions || [];

      const preselected = this.items.find((i) => i.isSelected);
      if (preselected) this.control.setValue(preselected.value);
    }

    if (this.dropDown.valueSource === 'external_references') {
      this.isExternalSelection = true;
      this.externalCategory = this.dropDown.externalCategory || '';
      this.externalField = this.dropDown.externalField || '';
      this.control.setValue('');
      this.control.updateValueAndValidity();
    }
  }

  async listenToReferences(_callback: () => void) {
    this.isLoading = true;

    try {
      // Get current value from BehaviorSubject (if available)
      const currentValue = await firstValueFrom(
        this.referenceCategoryService.allCategories.pipe(take(1))
      );

      // If we already have data, use it
      if (currentValue && currentValue.length > 0) {
        this.referenceCategories = currentValue;
        this.isLoading = false;
        _callback();
        return;
      }

      // Fetch categories with token (this will update the BehaviorSubject)
      await this.referenceCategoryService.getAllReferenceCategories(
        this.externalToken || undefined
      );

      // Get the updated data (will get the value that was just set)
      const data = await firstValueFrom(
        this.referenceCategoryService.allCategories.pipe(take(1))
      );

      if (data && data.length > 0) {
        this.referenceCategories = data;
        this.isLoading = false;
        _callback();
      } else {
        this.isLoading = false;
      }
    } catch (error) {
      console.error('Error loading reference categories:', error);
      this.isLoading = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.displayValueSubject.complete();
  }

  private async loadExternalReferences() {
    if (
      this.dropDown.valueSource !== 'external_references' ||
      !this.externalCategory ||
      !this.externalField
    ) {
      return;
    }

    this.isLoading = true;
    try {
      const data = await this.referenceService.getReferences(
        this.externalCategory,
        'all',
        false,
        'internal',
        this.externalToken || undefined
      );

      this.externalOptions = [];

      data?.forEach((dataItem) => {
        dataItem.reference?.forEach((ref) => {
          if (
            ref?.field_id.toString() === this.externalField &&
            !dataItem?.is_deleted
          ) {
            this.externalOptions.push({
              value: dataItem._id.toString(),
              displayValue: ref.value.toString(),
            });
          }
        });
      });

      this.displayValueSubject
        .pipe(
          startWith(''),
          map((value) => this._filterExternalOptions(value))
        )
        .subscribe((res) => {
          this.filteredExternalOptions = res;
        });
    } catch (error) {
      console.error('Error fetching references:', error);
    } finally {
      this.isLoading = false;
    }
  }

  ExternalReferenceAction(selectedOption: string) {
    // Find the actual ID value for the selected display value
    const selectedItem = this.externalOptions.find(
      (option) => option.displayValue === selectedOption
    );

    if (selectedItem) {
      // Store the ID in the control, but clear the display value
      this.control.setValue(selectedItem.value);
      this.externalDisplayValue = '';
      this.displayValueSubject.next('');
      this.valueChange.emit({
        isSelected: true,
        value: selectedItem.value,
      });
    }
  }

  onDisplayValueChange(value: string) {
    this.displayValueSubject.next(value || '');
  }

  private _filterExternalOptions(
    value: string
  ): ELAutocompleteElement<IReference>[] {
    const filterValue = value?.toLowerCase();
    return this.externalOptions.filter(
      (option) => option.displayValue.toLowerCase().indexOf(filterValue) === 0
    );
  }

  onSelectionChange(selected: string) {
    const selectedItem = this.items.find((i) => i.value === selected);

    if (selectedItem) {
      this.valueChange.emit(selectedItem);
    }
  }
}
